// SQLite renderer-side schema introspection.
//
// S7 items 2 + 8 — SQLite's shape is the odd one out. There is no bulk column
// catalog to page: every column / index / FK read is `PRAGMA <name>('<table>')`,
// one round-trip PER TABLE, already small. So the paging treatment splits in
// two:
//
//   * the `sqlite_master` relation list (and the ATTACHed-database UNION over
//     the same view) IS paged, `LIMIT n OFFSET m` keyed by `name`;
//   * the per-table PRAGMAs are walked in
//     `SQLITE_PRAGMA_BATCH_OBJECTS`-sized batches, with the merge + republish
//     + event-loop yield happening between batches. THAT is the chunking here
//     — a 20k-table database would otherwise fire 20k PRAGMAs from one
//     `Promise.all` and merge the lot in a single main-thread task.
//
// Every request goes through the shared per-connection introspection lane
// (`introspectionSemaphore.ts`, max 2 in flight) so the PRAGMA storm cannot
// starve a user query, and each artefact spends a TOTAL row budget across the
// whole walk (`introspectionBudgets.ts`) rather than a per-request cap.

import type {
  SqliteAttachedDatabase,
  SqliteSchemaColumn,
  SqliteSchemaForeignKey,
  SqliteSchemaIndex,
  SqliteSchemaObjectType,
  SqliteSchemaSnapshot,
} from './sqliteSchemaTypes';
import type { IntrospectionRow } from './introspectionPaging';
import { INTROSPECTION_PAGE_MAX_ROWS, yieldToEventLoop } from './introspectionPaging';
import {
  COLUMN_ROW_BUDGET,
  FK_ROW_BUDGET,
  INDEX_ROW_BUDGET,
  TABLE_ROW_BUDGET,
} from './introspectionBudgets';
import {
  fetchIntrospectionPage,
  fetchIntrospectionProbeRows,
  fetchPagedArtefact,
  type PagedFetchOptions,
} from './introspectionQuery';
import {
  reportIntrospectionTruncation,
  type TruncationArtefact,
} from './introspectionTruncation';

export type { PagedFetchOptions };

/**
 * Relations whose PRAGMAs are read (and merged, and published) per batch.
 * Sized so one batch is a handful of small round-trips and a merge far below
 * the 50 ms long-task threshold the perf gate asserts, while still amortising
 * the yield over more than one table.
 */
export const SQLITE_PRAGMA_BATCH_OBJECTS = 25;

/** One batch's worth of hydrated relations, handed to phase 2's merge. */
export type SqlitePageSink<TItem> = (items: readonly TItem[]) => void | Promise<void>;

function escapeSqliteStringLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

function quoteSqliteIdentifier(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * `PRAGMA <schema>.` prefix for an ATTACHed database, or `''` for the
 * connection's own database. SQLite puts the schema qualifier on the PRAGMA
 * name, never inside the table-name argument (`PRAGMA aux.table_info('t')`,
 * not `PRAGMA table_info('aux.t')`).
 */
function pragmaSchemaPrefix(schema?: string): string {
  return schema ? `${quoteSqliteIdentifier(schema)}.` : '';
}

function isBooleanTrue(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function toNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return String(value);
}

async function probeRows(
  connectionId: string,
  database: string,
  label: string,
  sql: string,
): Promise<readonly IntrospectionRow[]> {
  return fetchIntrospectionProbeRows({ engine: 'sqlite', connectionId, database, label, sql });
}

/**
 * One PRAGMA round-trip, through the introspection lane. `artefact` names what
 * a clip would degrade, so the pill can say "columns" / "indexes" rather than
 * "something in the schema list" — the gap S7 item 8 closed for these engines.
 */
async function pragmaRows(args: {
  connectionId: string;
  database: string;
  label: string;
  sql: string;
  artefact: TruncationArtefact;
}): Promise<readonly IntrospectionRow[]> {
  const page = await fetchIntrospectionPage({
    engine: 'sqlite',
    connectionId: args.connectionId,
    database: args.database,
    label: args.label,
    sql: args.sql,
  });
  if (!page.ok) return [];
  if (page.truncated) {
    reportIntrospectionTruncation({
      connectionId: args.connectionId,
      database: args.database,
      engine: 'sqlite',
      // A PRAGMA is not paged (it answers for ONE table), so the cap it could
      // exceed is the per-request page cap, not an artefact budget.
      cap: INTROSPECTION_PAGE_MAX_ROWS,
      artefact: args.artefact,
    });
  }
  return page.rows;
}

function mapPragmaTableInfo(rows: Record<string, unknown>[]): SqliteSchemaColumn[] {
  const columns = rows.map((row): SqliteSchemaColumn | null => {
    const name = String(row.name ?? '');
    if (!name) return null;

    const dataTypeRaw = String(row.type ?? '').trim();
    const dataType = dataTypeRaw.length > 0 ? dataTypeRaw : 'unknown';

    const notNull = isBooleanTrue(row.notnull);
    const pk = typeof row.pk === 'number' ? row.pk : Number(row.pk ?? 0);
    const isPrimaryKey = Number.isFinite(pk) && pk > 0;
    const cid = typeof row.cid === 'number' ? row.cid : Number(row.cid ?? 0);

    return {
      name,
      dataType,
      isNullable: !(notNull || isPrimaryKey),
      isPrimaryKey,
      defaultValue: toNullableString(row.dflt_value),
      ordinalPosition: Number.isFinite(cid) ? cid : 0,
    };
  });

  return columns
    .filter((column): column is SqliteSchemaColumn => column !== null)
    .sort((a, b) => a.ordinalPosition - b.ordinalPosition);
}

async function fetchObjectColumns(connectionId: string, database: string, objectName: string, schema?: string): Promise<SqliteSchemaColumn[]> {
  const escaped = escapeSqliteStringLiteral(objectName);
  const rows = await pragmaRows({
    connectionId,
    database,
    label: 'table-info',
    sql: `PRAGMA ${pragmaSchemaPrefix(schema)}table_info('${escaped}');`,
    artefact: 'columns',
  });
  return mapPragmaTableInfo(rows as Record<string, unknown>[]);
}

async function fetchTableIndexes(connectionId: string, database: string, tableName: string, schema?: string): Promise<SqliteSchemaIndex[]> {
  const escaped = escapeSqliteStringLiteral(tableName);
  const prefix = pragmaSchemaPrefix(schema);
  const listRows = await pragmaRows({
    connectionId,
    database,
    label: 'index-list',
    sql: `PRAGMA ${prefix}index_list('${escaped}');`,
    artefact: 'indexes',
  });
  // `origin` says what created the index: 'c' = a CREATE INDEX statement,
  // 'u' = a UNIQUE constraint, 'pk' = the PRIMARY KEY. Read from the SAME
  // rows already being scanned (no extra pragma) so the bus can tell the
  // primary-key index apart at the index-NAME slots.
  const indexRows = listRows
    .map((row) => {
      const name = String(row.name ?? '').trim();
      if (!name) return null;
      return {
        name,
        isUnique: isBooleanTrue(row.unique),
        isPrimary: String(row.origin ?? '').trim().toLowerCase() === 'pk',
      };
    })
    .filter((row): row is { name: string; isUnique: boolean; isPrimary: boolean } => row !== null);

  const indexes: SqliteSchemaIndex[] = [];
  for (const idx of indexRows) {
    const escapedIndex = escapeSqliteStringLiteral(idx.name);
    const infoRows = await pragmaRows({
      connectionId,
      database,
      label: 'index-info',
      sql: `PRAGMA ${prefix}index_info('${escapedIndex}');`,
      artefact: 'indexes',
    });
    const columns = infoRows
      .map((row) => String(row.name ?? '').trim())
      .filter(Boolean);
    indexes.push({ name: idx.name, isUnique: idx.isUnique, isPrimary: idx.isPrimary, columns });
  }

  return indexes;
}

async function fetchTableForeignKeys(connectionId: string, database: string, tableName: string, schema?: string): Promise<SqliteSchemaForeignKey[]> {
  const escaped = escapeSqliteStringLiteral(tableName);
  const rows = await pragmaRows({
    connectionId,
    database,
    label: 'fk-list',
    sql: `PRAGMA ${pragmaSchemaPrefix(schema)}foreign_key_list('${escaped}');`,
    artefact: 'foreignKeys',
  });
  return rows
    .map((row) => {
      const idRaw = typeof row.id === 'number' ? row.id : Number(row.id ?? 0);
      const id = Number.isFinite(idRaw) ? idRaw : 0;
      const referencedTable = String(row.table ?? '').trim();
      const from = String(row.from ?? '').trim();
      const to = String(row.to ?? '').trim();
      if (!referencedTable || !from) return null;
      return { id, referencedTable, from, to } satisfies SqliteSchemaForeignKey;
    })
    .filter((fk): fk is SqliteSchemaForeignKey => fk !== null);
}

/**
 * `PRAGMA database_list` → the ATTACHed databases, `main` and `temp`
 * dropped. Row shape is `(seq, name, file)`; `file` is `''` for `:memory:`
 * and for an anonymous temp database.
 *
 * Exported for unit coverage — the row mapper is the only part of the
 * attached-database walk that has no round-trip in it.
 */
export function mapDatabaseListRows(
  rows: readonly Record<string, unknown>[],
): SqliteAttachedDatabase[] {
  const out: SqliteAttachedDatabase[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const name = String(row.name ?? '').trim();
    if (!name) continue;
    const folded = name.toLowerCase();
    if (folded === 'main' || folded === 'temp') continue;
    if (seen.has(folded)) continue;
    seen.add(folded);
    out.push({ name, file: String(row.file ?? '') });
  }
  return out;
}

export async function fetchSqliteAttachedDatabases(
  connectionId: string,
  database: string,
): Promise<SqliteAttachedDatabase[]> {
  const rows = await probeRows(connectionId, database, 'database-list', 'PRAGMA database_list;');
  return mapDatabaseListRows(rows);
}

/**
 * The tables and views of every ATTACHed database, in ONE query — a
 * `UNION ALL` over each schema's own `sqlite_master`. The attach limit
 * (`SQLITE_MAX_ATTACHED`, 10 by default) bounds the branch count, so this
 * stays a single round-trip regardless of how many databases are attached.
 */
async function fetchAttachedObjectList(
  connectionId: string,
  database: string,
  attached: readonly SqliteAttachedDatabase[],
  options?: PagedFetchOptions,
): Promise<SqliteRelationEntry[]> {
  if (attached.length === 0) return [];
  const union = attached
    .map(
      (db) =>
        `SELECT '${escapeSqliteStringLiteral(db.name)}' AS schema_name, name, type, sql
         FROM ${quoteSqliteIdentifier(db.name)}.sqlite_master
         WHERE type IN ('table','view') AND name NOT LIKE 'sqlite_%'`,
    )
    .join('\nUNION ALL\n');
  const out: SqliteRelationEntry[] = [];
  await fetchPagedArtefact<SqliteRelationEntry>({
    engine: 'sqlite',
    dialect: 'limit-offset',
    connectionId,
    database,
    label: 'attached-relations',
    artefact: 'tables',
    totalBudget: TABLE_ROW_BUDGET,
    sqlForPage: (pageClause) => `${union}\nORDER BY schema_name, name ${pageClause};`,
    aggregate: (rows) => aggregateSqliteRelationRows(rows, true),
    sink: (page) => {
      out.push(...page);
    },
    shouldAbort: options?.shouldAbort,
  });
  return out;
}

export async function fetchSqliteServerVersion(connectionId: string, database: string): Promise<string> {
  const rows = await probeRows(connectionId, database, 'version', 'SELECT sqlite_version() AS version;');
  const row = rows[0];
  if (!row) return '';
  const value = row.version ?? Object.values(row)[0];
  return value == null ? '' : String(value);
}

/** One relation from `sqlite_master` — the phase-1 list item. */
export type SqliteRelationEntry = {
  name: string;
  type: SqliteSchemaObjectType;
  sql: string | null;
  /** The ATTACHed alias, or `undefined` for the connection's own database. */
  schema?: string;
};

export function aggregateSqliteRelationRows(
  rows: readonly IntrospectionRow[],
  attached: boolean,
): SqliteRelationEntry[] {
  const out: SqliteRelationEntry[] = [];
  for (const row of rows) {
    const name = String(row.name ?? '').trim();
    const type = String(row.type ?? '').trim().toLowerCase();
    if (!name) continue;
    if (type !== 'table' && type !== 'view') continue;
    if (attached) {
      const schema = String(row.schema_name ?? '').trim();
      if (!schema) continue;
      out.push({ schema, name, type, sql: toNullableString(row.sql) });
    } else {
      out.push({ name, type, sql: toNullableString(row.sql) });
    }
  }
  return out;
}

export function buildSqliteMasterPageSql(pageClause: string): string {
  return `SELECT name, type, sql
    FROM sqlite_master
    WHERE type IN ('table','view')
      AND name NOT LIKE 'sqlite_%'
    ORDER BY name
    ${pageClause}`;
}

/**
 * Page the `sqlite_master` relation list into `sink` — PHASE 1 of the tiered
 * load, plus the ATTACHed databases' own `sqlite_master` rows so `aux.tbl`
 * resolves from the first publish.
 */
export async function fetchSqliteRelationPages(
  connectionId: string,
  database: string,
  attached: readonly SqliteAttachedDatabase[],
  sink: SqlitePageSink<SqliteRelationEntry>,
  options?: PagedFetchOptions,
): Promise<void> {
  await fetchPagedArtefact<SqliteRelationEntry>({
    engine: 'sqlite',
    dialect: 'limit-offset',
    connectionId,
    database,
    label: 'relations',
    artefact: 'tables',
    totalBudget: TABLE_ROW_BUDGET,
    sqlForPage: buildSqliteMasterPageSql,
    aggregate: (rows) => aggregateSqliteRelationRows(rows, false),
    sink,
    shouldAbort: options?.shouldAbort,
  });
  if (options?.shouldAbort?.()) return;
  // ATTACHed databases (`ATTACH DATABASE 'aux.db' AS aux_db`) are part of the
  // connection's live namespace: `aux_db.tbl` resolves, and an unqualified
  // name resolves through them too.
  const attachedObjects = await fetchAttachedObjectList(
    connectionId,
    database,
    attached,
    options,
  ).catch(() => [] as SqliteRelationEntry[]);
  if (attachedObjects.length > 0) await sink(attachedObjects);
}

/** A relation with its PRAGMA-sourced detail attached. */
export type SqliteHydratedRelation = SqliteRelationEntry & {
  columns: SqliteSchemaColumn[];
  indexes: SqliteSchemaIndex[];
  foreignKeys: SqliteSchemaForeignKey[];
};

/**
 * PHASE 2 for SQLite: walk the relations in
 * `SQLITE_PRAGMA_BATCH_OBJECTS`-sized batches, reading each one's
 * `table_info` / `index_list` + `index_info` / `foreign_key_list`, and hand
 * every finished batch to `sink` with an event-loop yield after it. The batch
 * IS the page here — SQLite has no bulk column catalog to `LIMIT`.
 *
 * Each artefact spends a TOTAL row budget across the whole walk: once it is
 * exhausted the remaining relations skip that artefact and the clip is
 * reported once, naming it.
 */
export async function fetchSqliteRelationDetailBatches(
  connectionId: string,
  database: string,
  relations: readonly SqliteRelationEntry[],
  sink: SqlitePageSink<SqliteHydratedRelation>,
  options?: PagedFetchOptions & { readonly yieldBetweenBatches?: () => Promise<void> },
): Promise<void> {
  const yieldFn = options?.yieldBetweenBatches ?? yieldToEventLoop;
  const spent = { columns: 0, indexes: 0, foreignKeys: 0 };
  const reported = new Set<TruncationArtefact>();
  const budgets: Record<keyof typeof spent, number> = {
    columns: COLUMN_ROW_BUDGET,
    indexes: INDEX_ROW_BUDGET,
    foreignKeys: FK_ROW_BUDGET,
  };
  const withinBudget = (artefact: keyof typeof spent): boolean => {
    if (spent[artefact] < budgets[artefact]) return true;
    if (!reported.has(artefact)) {
      reported.add(artefact);
      reportIntrospectionTruncation({
        connectionId,
        database,
        engine: 'sqlite',
        cap: budgets[artefact],
        artefact,
      });
    }
    return false;
  };

  for (let start = 0; start < relations.length; start += SQLITE_PRAGMA_BATCH_OBJECTS) {
    if (options?.shouldAbort?.()) return;
    const batch = relations.slice(start, start + SQLITE_PRAGMA_BATCH_OBJECTS);
    const hydrated = await Promise.all(
      batch.map(async (obj): Promise<SqliteHydratedRelation> => {
        const columns = withinBudget('columns')
          ? await fetchObjectColumns(connectionId, database, obj.name, obj.schema)
          : [];
        spent.columns += columns.length;
        const isTable = obj.type === 'table';
        const indexes = isTable && withinBudget('indexes')
          ? await fetchTableIndexes(connectionId, database, obj.name, obj.schema)
          : [];
        spent.indexes += indexes.length;
        const foreignKeys = isTable && withinBudget('foreignKeys')
          ? await fetchTableForeignKeys(connectionId, database, obj.name, obj.schema)
          : [];
        spent.foreignKeys += foreignKeys.length;
        return { ...obj, columns, indexes, foreignKeys };
      }),
    );
    if (options?.shouldAbort?.()) return;
    await sink(hydrated);
    if (start + SQLITE_PRAGMA_BATCH_OBJECTS < relations.length) await yieldFn();
  }
}

/**
 * Build a COMPLETE snapshot in one call — phase 1 and phase 2 accumulated
 * without publishing. `sqliteSchemaCache` no longer uses this (it drives the
 * two phases itself so the relation list can publish first); it stays as the
 * one-shot form for callers that just want the finished object, and for the
 * unit tests that exercise the query set end to end.
 */
export async function buildSqliteSchemaSnapshot(connectionId: string, database: string): Promise<SqliteSchemaSnapshot> {
  const [serverVersion, attachedDatabases] = await Promise.all([
    fetchSqliteServerVersion(connectionId, database).catch(() => ''),
    fetchSqliteAttachedDatabases(connectionId, database).catch(() => [] as SqliteAttachedDatabase[]),
  ]);

  const relations: SqliteRelationEntry[] = [];
  await fetchSqliteRelationPages(connectionId, database, attachedDatabases, (page) => {
    relations.push(...page);
  });

  const hydrated: SqliteHydratedRelation[] = [];
  await fetchSqliteRelationDetailBatches(connectionId, database, relations, (batch) => {
    hydrated.push(...batch);
  });

  const tables = hydrated.filter((obj) => obj.type === 'table');
  const views = hydrated.filter((obj) => obj.type === 'view');

  const objectsByNameLower: SqliteSchemaSnapshot['objectsByNameLower'] = {};
  const columnsByObjectNameLower: SqliteSchemaSnapshot['columnsByObjectNameLower'] = {};
  const columnIndexByNameLower: SqliteSchemaSnapshot['columnIndexByNameLower'] = {};

  for (const obj of hydrated) {
    // Own-database objects win the unqualified index — SQLite resolves an
    // unqualified name against `main` before the attached databases, and the
    // hover / column-index consumers key on the bare name.
    const key = obj.name.toLowerCase();
    if (obj.schema && objectsByNameLower[key]) continue;
    objectsByNameLower[key] = obj;
    columnsByObjectNameLower[key] = obj.columns;

    for (const column of obj.columns) {
      const columnKey = column.name.toLowerCase();
      const list = columnIndexByNameLower[columnKey] ?? [];
      list.push({ objectName: obj.name, objectType: obj.type, column });
      columnIndexByNameLower[columnKey] = list;
    }
  }

  return {
    connectionId,
    database,
    builtAt: Date.now(),
    tables,
    views,
    objectsByNameLower,
    columnsByObjectNameLower,
    columnIndexByNameLower,
    routinesByNameLower: {},
    serverVersion,
    attachedDatabases,
    phase: 'complete',
  };
}
